import { useCallback, useRef } from "react";

import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import useUiuxStore from "src/store/useUiuxStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";

import {
  deletePRD2050_R01,
  getPRD2050_R01DetailList,
  getPRD2050_R01List,
  getPRD2050_R01LotList,
  postPRD2050_R01,
} from "./Api_PRD2050_R01";
import { PRD2050_R01Dto } from "./Dto_PRD2050_R01";
import { usePRD2050_R01Store } from "./Store_PRD2050_R01";

/**
 * PRD2050_R01의 조회, 행 선택, 모달 열기, 삭제 표시, 저장 흐름을 관리한다.
 * 화면 컴포넌트는 이 훅이 반환하는 이벤트 함수만 액션바와 그리드에 연결한다.
 */
export const Content_PRD2050_R01 = () => {
  const {
    PRD2050_R01Filter,
    setPRD2050_R01List,
    selectedPRD2050_R01,
    setSelectedPRD2050_R01,
    PRD2050_R01DetailList,
    setPRD2050_R01DetailList,
    selectedPRD2050_R01DetailList,
    initSelectedPRD2050_R01DetailList,
    removePRD2050_R01DetailList,
    deletedPRD2050_R01DetailList,
    addDeletedPRD2050_R01DetailList,
    removeDeletedPRD2050_R01DetailList,
    originalPRD2050_R01DetailList,
    setPRD2050_R01ModalList,
    setIsModalVisible,
    initData,
  } = usePRD2050_R01Store();
  const { setMessage } = useUiuxStore();
  const { setLoadingSpinner } = useLoadingSpinnerStore();
  // 늦게 도착한 상세 조회 응답이 현재 선택 행의 데이터를 덮지 않도록 요청 순번을 관리한다.
  const detailRequestIdRef = useRef(0);

  useFetchActionbarAuth("PRD2050_R01");

  const hasPendingChanges =
    PRD2050_R01DetailList.some((item) => item.inserted) ||
    deletedPRD2050_R01DetailList.length > 0;

  /** 선택한 작업지시의 상세 LOT을 조회하고 가장 최근 요청의 결과만 Store에 반영한다. */
  const fetchDetailList = useCallback(
    async (productOrder: PRD2050_R01Dto) => {
      const requestId = ++detailRequestIdRef.current;
      const response = await getPRD2050_R01DetailList(
        productOrder.productOrderNo,
      );
      if (requestId === detailRequestIdRef.current) {
        const detailList = response.data.data ?? [];
        setPRD2050_R01DetailList(detailList);
        if (detailList.length === 0) {
          showWarning("투입내역이 없습니다.");
        }
      }
    },
    [setPRD2050_R01DetailList],
  );

  /**
   * 작업지시 목록을 조회한다.
   * 조회 결과의 첫 행을 자동 선택한 뒤 해당 행의 상세 LOT까지 연속해서 조회한다.
   */
  const handleSearchClick = useCallback(async () => {
    if (hasPendingChanges) {
      showWarning("변경 내용을 저장하거나 초기화한 후 다시 조회해 주세요.");
      return;
    }

    detailRequestIdRef.current++;
    setLoadingSpinner(true);
    try {
      const response = await getPRD2050_R01List("search", PRD2050_R01Filter);
      const productOrderList = response.data.data ?? [];
      setPRD2050_R01List(productOrderList);
      setPRD2050_R01DetailList([]);
      setSelectedPRD2050_R01(undefined);

      // 목록 조회 직후 첫 작업지시와 상세 내역이 함께 보이도록 자동 선택한다.
      const firstProductOrder = productOrderList[0];
      if (firstProductOrder) {
        setSelectedPRD2050_R01(firstProductOrder);
        await fetchDetailList(firstProductOrder);
        ShowSuccessFetch(
          setMessage,
          `조회가 완료되었습니다. (총 ${productOrderList.length}건)`,
        );
        setTimeout(() => setMessage(""), 5000);
      } else {
        setMessage("");
        showWarning("조회된 데이터가 없습니다.");
      }
    } finally {
      setLoadingSpinner(false);
    }
  }, [
    PRD2050_R01Filter,
    hasPendingChanges,
    fetchDetailList,
    setLoadingSpinner,
    setMessage,
    setPRD2050_R01DetailList,
    setPRD2050_R01List,
    setSelectedPRD2050_R01,
  ]);

  /**
   * 작업지시 단일 클릭을 처리한다.
   * 새 행 또는 삭제 표시가 있으면 선택 이동을 막고, 같은 행을 다시 누르면 선택을 해제한다.
   */
  const handleOrderRowClick = useCallback(
    async (productOrder: PRD2050_R01Dto) => {
      if (selectedPRD2050_R01?.productOrderNo === productOrder.productOrderNo) {
        if (hasPendingChanges) {
          showWarning("변경 내용을 저장하거나 초기화한 후 선택을 해제해 주세요.");
          return false;
        }

        detailRequestIdRef.current++;
        setSelectedPRD2050_R01(undefined);
        setPRD2050_R01DetailList([]);
        return false;
      }
      if (hasPendingChanges) {
        showWarning("변경 내용을 저장하거나 초기화한 후 다른 작업지시서를 선택해 주세요.");
        return false;
      }

      setSelectedPRD2050_R01(productOrder);
      setPRD2050_R01DetailList([]);
      await fetchDetailList(productOrder);
      return true;
    },
    [
      hasPendingChanges,
      fetchDetailList,
      selectedPRD2050_R01?.productOrderNo,
      setPRD2050_R01DetailList,
      setSelectedPRD2050_R01,
    ],
  );

  /** 투입 가능한 LOT을 조회하고 이미 하단 상세에 담긴 LOT을 제외한 뒤 모달을 연다. */
  const openPRD2050_R01MaterialLotModal = useCallback(async (
    productOrder: PRD2050_R01Dto,
  ) => {
    const response = await getPRD2050_R01LotList(
      productOrder.productOrderNo,
    );
    const assignedKeySet = new Set(
      PRD2050_R01DetailList.map((item) => `${item.itemCd}|${item.lotNo}`),
    );
    setPRD2050_R01ModalList(
      (response.data.data ?? []).filter(
        (item) => !assignedKeySet.has(`${item.itemCd}|${item.lotNo}`),
      ),
    );
    setIsModalVisible(true);
  }, [
    PRD2050_R01DetailList,
    setIsModalVisible,
    setPRD2050_R01ModalList,
  ]);

  /** 액션바 등록 버튼으로 LOT 선택 모달을 연다. */
  const handleModalOpenClick = useCallback(async () => {
    if (!selectedPRD2050_R01?.productOrderNo) {
      showWarning("작업지시서를 먼저 선택해 주세요.");
      return;
    }
    await openPRD2050_R01MaterialLotModal(selectedPRD2050_R01);
  }, [openPRD2050_R01MaterialLotModal, selectedPRD2050_R01]);

  /** 작업지시 더블 클릭 시 해당 행을 선택하고 LOT 선택 모달을 연다. */
  const handleOrderRowDoubleClick = useCallback(
    async (productOrder: PRD2050_R01Dto) => {
      if (selectedPRD2050_R01?.productOrderNo === productOrder.productOrderNo) {
        await openPRD2050_R01MaterialLotModal(productOrder);
        return;
      }

      const isAccepted = await handleOrderRowClick(productOrder);
      if (isAccepted) {
        await openPRD2050_R01MaterialLotModal(productOrder);
      }
    },
    [
      handleOrderRowClick,
      openPRD2050_R01MaterialLotModal,
      selectedPRD2050_R01?.productOrderNo,
    ],
  );

  /**
   * 상세 행의 삭제 상태를 전환한다.
   * 신규 행은 즉시 목록에서 제거하고, 서버 조회 행은 저장 시 삭제할 목록에 표시한다.
   */
  const handleDeleteClick = useCallback(() => {
    // 삭제 표시된 행이 있으면 삭제 버튼을 '삭제 취소'로 동작시킨다.
    if (deletedPRD2050_R01DetailList.length > 0) {
      removeDeletedPRD2050_R01DetailList(
        deletedPRD2050_R01DetailList,
      );
      initSelectedPRD2050_R01DetailList();
      return;
    }

    const selectedList = PRD2050_R01DetailList.filter((item) =>
      selectedPRD2050_R01DetailList.some(
        (valueIndex) => Number(valueIndex) === item.valueIndex,
      ),
    );

    if (selectedList.length === 0) {
      showWarning("삭제할 사입 LOT을 선택해 주세요.");
      return;
    }

    const insertedValueIndexList = selectedList
      .filter((item) => item.inserted)
      .map((item) => item.valueIndex.toString());

    const existingValueIndexList = selectedPRD2050_R01DetailList.filter(
      (valueIndex) =>
        selectedList.some(
          (item) =>
            item.valueIndex === Number(valueIndex) && !item.inserted,
        ),
    );

    if (existingValueIndexList.length > 0) {
      addDeletedPRD2050_R01DetailList(existingValueIndexList);
    }
    if (insertedValueIndexList.length > 0) {
      removePRD2050_R01DetailList(insertedValueIndexList);
    }
    initSelectedPRD2050_R01DetailList();
  }, [
    PRD2050_R01DetailList,
    addDeletedPRD2050_R01DetailList,
    deletedPRD2050_R01DetailList,
    initSelectedPRD2050_R01DetailList,
    removePRD2050_R01DetailList,
    removeDeletedPRD2050_R01DetailList,
    selectedPRD2050_R01DetailList,
  ]);

  /**
   * 신규 행과 삭제 표시 행을 각각 등록·삭제 API로 전달한다.
   * 저장 완료 후 서버 상세 목록을 다시 조회해 화면 상태와 DB 상태를 동기화한다.
   */
  const handleSaveClick = useCallback(async () => {
    const productOrderNo = selectedPRD2050_R01?.productOrderNo;
    if (!productOrderNo) {
      showWarning("작업지시서를 먼저 선택해 주세요.");
      return;
    }
    if (!hasPendingChanges) {
      showWarning("저장할 변경사항이 없습니다.");
      return;
    }

    setLoadingSpinner(true);
    try {
      // inserted는 화면 상태이므로 API 변환 단계에서 서버 DTO 필드만 전달된다.
      const insertedList = PRD2050_R01DetailList
        .filter((item) => item.inserted)
        .map((item) => ({ ...item, productOrderNo }));
      const deletedList = originalPRD2050_R01DetailList
        .filter((item) =>
          deletedPRD2050_R01DetailList.some(
            (valueIndex) => Number(valueIndex) === item.valueIndex,
          ),
        )
        .map((item) => ({ ...item, productOrderNo }));

      // 등록과 삭제는 서로 다른 목록이며, 존재하는 작업만 병렬 호출한다.
      await Promise.all([
        insertedList.length > 0
          ? postPRD2050_R01(insertedList)
          : Promise.resolve(),
        deletedList.length > 0
          ? deletePRD2050_R01(deletedList)
          : Promise.resolve(),
      ]);

      const response = await getPRD2050_R01DetailList(productOrderNo);
      detailRequestIdRef.current++;
      setPRD2050_R01DetailList(response.data.data ?? []);
      ShowSuccessFetch(setMessage, "사입 LOT 저장이 완료되었습니다.");
      setTimeout(() => setMessage(""), 5000);
    } finally {
      setLoadingSpinner(false);
    }
  }, [
    PRD2050_R01DetailList,
    deletedPRD2050_R01DetailList,
    hasPendingChanges,
    originalPRD2050_R01DetailList,
    selectedPRD2050_R01,
    setLoadingSpinner,
    setMessage,
    setPRD2050_R01DetailList,
  ]);

  /** 진행 중인 상세 요청을 무효화하고 화면의 모든 Store 상태와 메시지를 초기화한다. */
  const handleInitClick = useCallback(() => {
    // detailRequestIdRef.current++;
    initData();
    setMessage("");
  }, [initData, setMessage]);

  return {
    handleSearchClick,
    handleOrderRowClick,
    handleOrderRowDoubleClick,
    handleModalOpenClick,
    handleDeleteClick,
    handleSaveClick,
    initData: handleInitClick,
  };
};
